import React from "react";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  SendOutlined,
  SyncOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Tag } from "antd";
import { useTranslation } from "react-i18next";

const statusMeta: Record<string, { color: string; icon: React.ReactNode }> = {
  draft: { color: "default", icon: <FileTextOutlined /> },
  collecting_documents: { color: "processing", icon: <SyncOutlined spin /> },
  validated: { color: "success", icon: <CheckCircleOutlined /> },
  issues_found: { color: "error", icon: <CloseCircleOutlined /> },
  resolving: { color: "warning", icon: <WarningOutlined /> },
  passport_ready: { color: "cyan", icon: <CheckCircleOutlined /> },
  shared: { color: "purple", icon: <SendOutlined /> },
};

const severityMeta: Record<string, { color: string; icon: React.ReactNode }> = {
  blocker: { color: "red", icon: <CloseCircleOutlined /> },
  warning: { color: "orange", icon: <ExclamationCircleOutlined /> },
  info: { color: "blue", icon: <InfoCircleOutlined /> },
};

type CaseStatusProps = {
  status: string;
  className?: string;
};

/** US-701: case status Tag with icon + translated label (not color-only). */
export function CaseStatusBadge({ status, className }: CaseStatusProps) {
  const { t } = useTranslation();
  const meta = statusMeta[status] || { color: "default", icon: <ClockCircleOutlined /> };
  const label = t(`compliance.status.${status}`, { defaultValue: status });
  return (
    <Tag
      color={meta.color}
      icon={meta.icon}
      className={className}
      data-testid={`case-status-${status}`}
      aria-label={label}
    >
      {label}
    </Tag>
  );
}

type SeverityProps = {
  severity: string;
};

/** US-701: validation finding severity with icon + label. */
export function SeverityBadge({ severity }: SeverityProps) {
  const { t } = useTranslation();
  const meta = severityMeta[severity] || { color: "default", icon: <InfoCircleOutlined /> };
  const label = t(`compliance.severity.${severity}`, { defaultValue: severity });
  return (
    <Tag color={meta.color} icon={meta.icon} data-testid={`severity-${severity}`} aria-label={label}>
      {label}
    </Tag>
  );
}
